"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";

const CountUp = dynamic(() => import("react-countup"), {
  ssr: false,
  loading: () => <span>0</span>,
});

interface StatItem {
  target: number;
  suffix: string;
  prefix?: string;
  label: string;
}

interface AnimatedStatsProps {
  stats: StatItem[];
}

export default function AnimatedStats({ stats }: AnimatedStatsProps) {
  const [startAnimation, setStartAnimation] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    const element = ref.current;
    if (!element) return;

    const triggerAnimation = () => {
      if (hasTriggeredRef.current) return;
      hasTriggeredRef.current = true;
      setStartAnimation(true);
    };

    // Use IntersectionObserver to detect when element enters viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add small delay to ensure parent GSAP animations have started
            setTimeout(triggerAnimation, 300);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" }
    );

    observer.observe(element);

    // Fallback: trigger animation after mount if already visible
    const fallbackTimeout = setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (isInViewport) {
        triggerAnimation();
      }
    }, 1500);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimeout);
    };
  }, [isClient]);

  return (
    <div ref={ref} className="grid grid-cols-2 md:grid-cols-4 md:w-fit  ">
      {stats &&
        stats.length > 0 &&
        stats.map((stat, index) => (
          <div
            key={index}
            className={`text-left p-2 md:pr-[34px] md:py-[32px] md:pl-[32px] md:max-w-[234px] ${
              index < stats.length - 1 ? "md:border-r-2 border-[#D1D5DB]" : ""
            }`}
          >
            <h3 className="lg:text-[48px] lg:leading-[48px] text-[32px] leading-[32px] font-semibold text-primary text-center lg:text-left ">
              {isClient && startAnimation ? (
                <CountUp
                  start={0}
                  end={stat.target}
                  prefix={stat.prefix || ""}
                  suffix={stat.suffix || ""}
                  separator=","
                  duration={2}
                  delay={index * 0.1}
                  preserveValue={true}
                />
              ) : (
                `${stat.prefix || ""}0${stat.suffix || ""}`
              )}
            </h3>
            <p className="mt-2 lg:text-sm text-xs text-[#4B5563] leading-[20px] text-center lg:text-left max-md:max-w-[150px] max-md:mx-auto">
              {stat.label}
            </p>
          </div>
        ))}
    </div>
  );
}
